import { buildNamespaces } from "./namespaces";
import {
  emptyMetrics,
  parseTagDailyActivity,
  parseTagList,
  parseTagSpendByEntity,
  parseTeamDailyActivity,
  totalPages,
} from "./parse";
import { groupTagsByNamespace, parseTag } from "./tags";
import type { DateOnly } from "./dates";
import type {
  RankedTagSpendResult,
  TagDetailsResult,
  TagUsage,
  TagUsageRow,
  TeamUsage,
  TeamWindowUsage,
  UsageDay,
  UsageDimensionRow,
  UsageMetrics,
} from "./types";

/**
 * Fetch and assemble per-tag usage from the LiteLLM proxy.
 *
 * ## Rank first, then fetch detail
 *
 * The obvious shape — discover every tag, then fetch detail for all of them — does not
 * survive contact with real data. `node:` values are workflow-step ids that churn on every
 * republish, so a busy environment accumulates thousands; chunked detail fetches for all of
 * them would time out against a proxy that runs single-instance, and leave `truncated`
 * permanently set.
 *
 * So discovery ranks first: one unfiltered `/tag/daily/activity` read — the same endpoint the
 * detail fetch uses, minus the `tags` filter — summed per tag across the window. Detail is
 * then fetched only for the top N per namespace. The tags that cost nothing are exactly the
 * ones nobody needs a daily breakdown of.
 *
 * Ranking used to come from `/global/spend/tags`, which was wrong twice over (Sentry
 * LITELLM-PROD-1G). It is admin-only — `global_spend_tracking_routes` in LiteLLM's
 * `proxy/_types.py`, deliberately NOT in `internal_user_view_only_routes` — so the
 * `internal_user_viewer` reporting user on every environment's key was rejected on every
 * page load (observed `401`, "Only proxy admin can be used…"). And it reads the
 * `DailyTagSpend` view over `LiteLLM_SpendLogs`, a table our proxy disables
 * (`general_settings.disable_spend_logs`), so even an admin key would have ranked an empty
 * list. `/tag/daily/activity` is viewer-allowed and reads `LiteLLM_DailyTagSpend`, which is
 * written unconditionally.
 *
 * `/tag/list` is the fallback when that read fails or reports no tags, and it is strictly
 * worse: it has no spend ordering, so truncating it drops an arbitrary subset rather than
 * the cheapest. That is surfaced as `truncated`, never hidden.
 */

/**
 * Injected so the orchestration is testable without a network.
 *
 * Timeouts and retries belong to the implementation, not here. This package deliberately
 * owns no transport concerns — that is the whole point of taking the fetch as a parameter —
 * and it keeps the package free of any DOM lib dependency.
 */
export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string> },
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

export interface CollectOptions {
  origin: string;
  apiKey: string;
  startDate: DateOnly;
  endDate: DateOnly;
  fetchImpl: FetchLike;
  /**
   * Namespaces to omit entirely: no detail is fetched for them and they do not appear in the
   * result (nor in `ignoredTags` — they are known, just unwanted). This is how consumers show
   * different dimension sets: the ERP page hides operator-internal namespaces, the control
   * plane shows everything. Default: none.
   */
  omitNamespaces?: readonly string[];
  /** Max tags to fetch detail for, per namespace. */
  topPerNamespace?: number;
  /** Tags per `/tag/daily/activity` request. */
  tagsPerRequest?: number;
  /** Hard stop on the pagination loop. */
  maxPages?: number;
}

interface ReaderOptions {
  origin: string;
  apiKey: string;
  startDate: DateOnly;
  endDate: DateOnly;
  fetchImpl: FetchLike;
}

export interface CollectRankedTagSpendOptions extends ReaderOptions {
  /**
   * Return only tags whose raw namespace matches this value. Omit to return every tag,
   * which lets callers share one unfiltered proxy read across multiple namespaces.
   */
  namespace?: string;
  maxPages?: number;
}

export interface CollectTagDetailsOptions extends ReaderOptions {
  /** The exact finite tag set to fetch. No namespace interpretation is applied. */
  tags: readonly string[];
  tagsPerRequest?: number;
  maxPages?: number;
}

export interface CollectTeamUsageOptions extends ReaderOptions {
  maxPages?: number;
}

const DEFAULT_TOP_PER_NAMESPACE = 25;
const DEFAULT_TAGS_PER_REQUEST = 25;
const DEFAULT_MAX_PAGES = 20;
/** Large enough that a normal window needs one page; the loop is the real guarantee. */
const PAGE_SIZE = 1000;

/**
 * Read every tag's spend from unfiltered `/tag/daily/activity`, optionally selecting one
 * raw namespace after all pages have been merged.
 */
export async function collectRankedTagSpend(
  options: CollectRankedTagSpendOptions,
): Promise<RankedTagSpendResult> {
  const result = await collectRankedTagSpendWithGetter(
    createGetter(options),
    dateRange(options),
    pageLimit(options.maxPages),
    options.namespace,
  );
  return {
    available: result.available,
    complete: result.complete,
    tags: result.tags,
  };
}

/** Read detail for an explicit tag list, chunking and paginating without classifying tags. */
export async function collectTagDetails(
  options: CollectTagDetailsOptions,
): Promise<TagDetailsResult> {
  const tags = [...new Set(options.tags)];
  if (tags.length === 0) {
    return { available: true, complete: true, rows: [], unexpectedTags: [] };
  }

  const get = createGetter(options);
  const range = dateRange(options);
  const maxPages = pageLimit(options.maxPages);
  const tagsPerRequest = positiveInteger(
    options.tagsPerRequest,
    DEFAULT_TAGS_PER_REQUEST,
  );
  const rows = new Map<string, TagUsageRow>();
  const unexpected = new Set<string>();
  let available = false;
  let complete = true;

  for (let i = 0; i < tags.length; i += tagsPerRequest) {
    const chunk = tags.slice(i, i + tagsPerRequest);
    const query = `tags=${chunk.map(encodeURIComponent).join(",")}&${range}`;
    let page = 1;
    let pages = 1;

    do {
      const payload = await get(
        `/tag/daily/activity?${query}&page=${page}&page_size=${PAGE_SIZE}`,
      );
      if (payload === null) {
        complete = false;
        break;
      }

      available = true;
      const parsed = parseTagDailyActivity(payload, chunk);
      mergeRows(rows, parsed.rows);
      for (const tag of parsed.unexpectedTags) unexpected.add(tag);

      const reportedPages = totalPages(payload);
      if (reportedPages > maxPages) complete = false;
      pages = Math.min(Math.max(pages, reportedPages), maxPages);
      page += 1;
    } while (page <= pages);
  }

  const detailRows = [...rows.values()];
  for (const row of detailRows) normalizeTagRow(row);
  detailRows.sort((a, b) => b.metrics.spend - a.metrics.spend);

  return {
    available,
    complete: available && complete,
    rows: detailRows,
    unexpectedTags: [...unexpected].sort(),
  };
}

/** Read and merge a paginated `/team/daily/activity` window. */
export async function collectTeamUsage(
  options: CollectTeamUsageOptions,
): Promise<TeamUsage> {
  const get = createGetter(options);
  const range = dateRange(options);
  const maxPages = pageLimit(options.maxPages);
  const result = emptyTeamUsage();
  const daily = new Map<string, UsageDay>();
  const byTeam = new Map<string, TeamWindowUsage>();
  const models = new Map<string, UsageDimensionRow>();
  const modelGroups = new Map<string, UsageDimensionRow>();
  const providers = new Map<string, UsageDimensionRow>();
  let page = 1;
  let pages = 1;
  let complete = true;

  do {
    const payload = await get(
      `/team/daily/activity?${range}&page=${page}&page_size=${PAGE_SIZE}`,
    );
    if (payload === null) {
      complete = false;
      break;
    }

    result.available = true;
    const parsed = parseTeamDailyActivity(payload);
    addMetrics(result.totals, parsed.totals);
    mergeDays(daily, parsed.daily);
    mergeTeams(byTeam, parsed.byTeam);
    mergeDimensions(models, parsed.models);
    mergeDimensions(modelGroups, parsed.modelGroups);
    mergeDimensions(providers, parsed.providers);
    result.availableDimensions.models ||= parsed.availableDimensions.models;
    result.availableDimensions.modelGroups ||=
      parsed.availableDimensions.modelGroups;
    result.availableDimensions.providers ||=
      parsed.availableDimensions.providers;

    const reportedPages = totalPages(payload);
    if (reportedPages > maxPages) complete = false;
    pages = Math.min(Math.max(pages, reportedPages), maxPages);
    page += 1;
  } while (page <= pages);

  result.complete = result.available && complete;
  result.daily = [...daily.values()].sort(compareDays);
  result.byTeam = [...byTeam.values()].sort(compareMetricRows);
  result.models = [...models.values()].sort(compareMetricRows);
  result.modelGroups = [...modelGroups.values()].sort(compareMetricRows);
  result.providers = [...providers.values()].sort(compareMetricRows);
  return result;
}

export async function collectTagUsage(
  options: CollectOptions,
): Promise<TagUsage> {
  const {
    startDate,
    endDate,
    omitNamespaces = [],
    topPerNamespace = DEFAULT_TOP_PER_NAMESPACE,
    tagsPerRequest = DEFAULT_TAGS_PER_REQUEST,
    maxPages = DEFAULT_MAX_PAGES,
  } = options;

  const warnings: string[] = [];
  const get = createGetter(options);
  const range = dateRange(options);

  const unavailable = (reason: string): TagUsage => ({
    configured: true,
    available: false,
    startDate,
    endDate,
    windowTotals: emptyMetrics(),
    denominatorSource: "none",
    namespaces: [],
    ignoredTags: [],
    warnings: [...warnings, reason],
  });

  // Discovery and team totals are independent reads. Starting them together prevents a tag
  // endpoint failure from suppressing the separate team endpoint.
  const [discovery, teamUsage] = await Promise.all([
    discoverTags(get, range, pageLimit(maxPages)),
    collectTeamUsage({ ...options, maxPages }),
  ]);
  if (discovery === null) {
    return unavailable("tag-discovery-unavailable");
  }
  const unranked = !discovery.rankedBySpend && discovery.tags.length > 0;
  if (unranked) warnings.push("ranking-unavailable");

  const { byNamespace, ignored } = groupTagsByNamespace(discovery.tags);
  // Dropped before the detail fan-out, so an omitted namespace costs no proxy requests.
  for (const namespace of omitNamespaces) byNamespace.delete(namespace);

  // ── cap per namespace, preserving rank when we have it ────────────────────
  const { selected, truncatedNamespaces } = selectTopTags(
    byNamespace,
    topPerNamespace,
  );

  const details = await collectTagDetails({
    ...options,
    tags: selected,
    tagsPerRequest,
    maxPages,
  });
  const team = teamUsage.available && teamUsage.complete ? teamUsage : null;
  // Empty attribution still needs a successful denominator read: untagged traffic can
  // have real spend, and a failed total must never be presented as a confirmed zero.
  if (selected.length === 0 && team === null) {
    return unavailable("denominator-unavailable");
  }
  if (team === null) warnings.push("denominator-unavailable");
  if (details.unexpectedTags.length > 0) warnings.push("unexpected-entities");

  // ── fold into namespaces ──────────────────────────────────────────────────
  const rowsByNamespace = foldRowsByNamespace(
    new Map(details.rows.map((row) => [row.tag, row])),
  );
  const silentTagCounts = countSilentTags(
    byNamespace,
    rowsByNamespace,
    topPerNamespace,
  );

  const built = buildNamespaces({
    rowsByNamespace,
    windowTotals: team?.totals ?? null,
    truncatedNamespaces,
    silentTagCounts,
  });

  return {
    configured: true,
    available: true,
    startDate,
    endDate,
    windowTotals: team?.totals ?? emptyMetrics(),
    denominatorSource: team ? "team-daily-activity" : "none",
    namespaces: built.namespaces,
    ignoredTags: ignored.sort(),
    warnings: [
      ...warnings,
      ...built.warnings,
      ...(unranked ? ["truncation-is-arbitrary"] : []),
    ],
  };
}

type Getter = (path: string) => Promise<unknown | null>;

/**
 * Which tags exist in the window, ranked by spend when the proxy will say.
 *
 * Returns `null` when discovery fails. A successful empty list is a valid window with no
 * tags, including the default window ending yesterday when routing was enabled today.
 */
async function discoverTags(
  get: Getter,
  range: string,
  maxPages: number,
): Promise<{ tags: string[]; rankedBySpend: boolean } | null> {
  const ranked = await collectRankedTagSpendWithGetter(get, range, maxPages);
  if (ranked.available && !ranked.failed && ranked.tags.length > 0) {
    return {
      tags: ranked.tags.map(({ tag }) => tag),
      rankedBySpend: true,
    };
  }

  const listed = parseTagList(await get(`/tag/list?${range}`));
  if (listed === null) return null;
  return { tags: listed, rankedBySpend: false };
}

/**
 * Every tag in the window, costliest first, from `/tag/daily/activity` with no `tags` filter.
 *
 * Paged like the detail fetch and under the same `maxPages` stop, because the endpoint pages
 * over `LiteLLM_DailyTagSpend` rows — one per (date, tag, key, model, …) — not over days.
 * Only the resulting ORDER is used: the detail fetch re-reads every number per tag.
 *
 * A page failure leaves the public partial result available but incomplete. The legacy
 * collector additionally records that failure internally so it can fall back to `/tag/list`
 * rather than selecting from half a ranking. Hitting `maxPages` is different: that was the
 * collector's previous explicit cap, so it keeps the capped ranking while the public result
 * honestly reports `complete: false`.
 */
async function collectRankedTagSpendWithGetter(
  get: Getter,
  range: string,
  maxPages: number,
  namespace?: string,
): Promise<RankedTagSpendResult & { failed: boolean }> {
  const spendByTag = new Map<string, number>();
  let page = 1;
  let pages = 1;
  let available = false;
  let complete = true;
  let failed = false;

  do {
    const payload = await get(
      `/tag/daily/activity?${range}&page=${page}&page_size=${PAGE_SIZE}`,
    );
    if (payload === null) {
      complete = false;
      failed = true;
      break;
    }

    available = true;
    for (const [tag, spend] of parseTagSpendByEntity(payload)) {
      if (namespace !== undefined && rawTagNamespace(tag) !== namespace) {
        continue;
      }
      spendByTag.set(tag, (spendByTag.get(tag) ?? 0) + spend);
    }

    const reportedPages = totalPages(payload);
    if (reportedPages > maxPages) complete = false;
    pages = Math.min(Math.max(pages, reportedPages), maxPages);
    page += 1;
  } while (page <= pages);

  return {
    available,
    complete: available && complete,
    failed,
    tags: [...spendByTag]
      .map(([tag, spend]) => ({ tag, spend }))
      .sort((a, b) => b.spend - a.spend || a.tag.localeCompare(b.tag)),
  };
}

function rawTagNamespace(tag: string): string {
  const separator = tag.indexOf(":");
  return separator > 0 ? tag.slice(0, separator) : tag;
}

/**
 * Cap each namespace to its top N, recording which ones lost rows.
 *
 * Relies on discovery order: when it was ranked, the kept tags are the costliest. When it was
 * not, the caller has already warned that truncation is arbitrary.
 */
function selectTopTags(
  byNamespace: Map<string, { tag: string }[]>,
  topPerNamespace: number,
): { selected: string[]; truncatedNamespaces: Set<string> } {
  const truncatedNamespaces = new Set<string>();
  const selected: string[] = [];

  for (const [namespace, tags] of byNamespace) {
    if (tags.length > topPerNamespace) truncatedNamespaces.add(namespace);
    selected.push(...tags.slice(0, topPerNamespace).map((tag) => tag.tag));
  }

  return { selected, truncatedNamespaces };
}

/** Bucket the fetched rows by namespace, normalising folded legacy values. */
function foldRowsByNamespace(
  rows: Map<string, TagUsageRow>,
): Map<string, TagUsageRow[]> {
  const rowsByNamespace = new Map<string, TagUsageRow[]>();

  for (const row of rows.values()) {
    const parsed = parseTag(row.tag);
    const namespace = parsed?.namespace ?? row.namespace;
    // Keep the folded legacy value (`backend` → `svc:backend`) rather than the raw tag, so a
    // pre-migration row lands in the same section as its modern equivalent.
    if (parsed) row.value = parsed.value;
    const bucket = rowsByNamespace.get(namespace) ?? [];
    bucket.push(row);
    rowsByNamespace.set(namespace, bucket);
  }

  return rowsByNamespace;
}

/**
 * Tags that exist but had no spend in the window.
 *
 * Counted against what was actually *requested* (the capped set), not everything discovered —
 * otherwise a truncated namespace would report its dropped tags as silent, conflating "cost
 * nothing" with "we did not ask".
 */
function countSilentTags(
  byNamespace: Map<string, { tag: string }[]>,
  rowsByNamespace: Map<string, TagUsageRow[]>,
  topPerNamespace: number,
): Map<string, number> {
  const silentTagCounts = new Map<string, number>();

  for (const [namespace, tags] of byNamespace) {
    const withSpend = rowsByNamespace.get(namespace)?.length ?? 0;
    silentTagCounts.set(
      namespace,
      Math.max(0, Math.min(tags.length, topPerNamespace) - withSpend),
    );
  }

  return silentTagCounts;
}

function mergeRows(
  into: Map<string, TagUsageRow>,
  from: Map<string, TagUsageRow>,
): void {
  for (const [tag, row] of from) {
    const existing = into.get(tag);
    if (!existing) {
      into.set(tag, row);
      continue;
    }
    existing.metrics.spend += row.metrics.spend;
    existing.metrics.promptTokens += row.metrics.promptTokens;
    existing.metrics.completionTokens += row.metrics.completionTokens;
    existing.metrics.cacheReadTokens += row.metrics.cacheReadTokens;
    existing.metrics.cacheCreationTokens += row.metrics.cacheCreationTokens;
    existing.metrics.requests += row.metrics.requests;
    existing.metrics.successfulRequests += row.metrics.successfulRequests;
    existing.metrics.failedRequests += row.metrics.failedRequests;
    existing.daily.push(...row.daily);
    existing.origins.push(...row.origins);
  }
}

function normalizeTagRow(row: TagUsageRow): void {
  const daily = new Map<string, UsageDay>();
  mergeDays(daily, row.daily);
  row.daily = [...daily.values()].sort(compareDays);

  const origins = new Map<string, TagUsageRow["origins"][number]>();
  for (const origin of row.origins) {
    const existing = origins.get(origin.keyAlias);
    if (existing) {
      existing.spend += origin.spend;
    } else {
      origins.set(origin.keyAlias, { ...origin });
    }
  }
  row.origins = [...origins.values()].sort((a, b) => b.spend - a.spend);
}

function createGetter(options: ReaderOptions): Getter {
  const origin = options.origin.replace(/\/+$/, "");
  return async (path) => {
    try {
      const response = await options.fetchImpl(`${origin}${path}`, {
        headers: { Authorization: `Bearer ${options.apiKey}` },
      });
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  };
}

function dateRange(
  options: Pick<ReaderOptions, "startDate" | "endDate">,
): string {
  return `start_date=${options.startDate}&end_date=${options.endDate}`;
}

function positiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

function pageLimit(value: number | undefined): number {
  return positiveInteger(value, DEFAULT_MAX_PAGES);
}

function addMetrics(into: UsageMetrics, from: UsageMetrics): void {
  into.spend += from.spend;
  into.promptTokens += from.promptTokens;
  into.completionTokens += from.completionTokens;
  into.cacheReadTokens += from.cacheReadTokens;
  into.cacheCreationTokens += from.cacheCreationTokens;
  into.requests += from.requests;
  into.successfulRequests += from.successfulRequests;
  into.failedRequests += from.failedRequests;
}

function mergeDays(
  into: Map<string, UsageDay>,
  from: readonly UsageDay[],
): void {
  for (const day of from) {
    const existing = into.get(day.date) ?? {
      date: day.date,
      spend: 0,
      requests: 0,
      totalTokens: 0,
    };
    existing.spend += day.spend;
    existing.requests += day.requests;
    existing.totalTokens += day.totalTokens;
    into.set(day.date, existing);
  }
}

function mergeTeams(
  into: Map<string, TeamWindowUsage>,
  from: readonly TeamWindowUsage[],
): void {
  for (const team of from) {
    const existing = into.get(team.teamId) ?? {
      teamId: team.teamId,
      teamAlias: null,
      metrics: emptyMetrics(),
    };
    addMetrics(existing.metrics, team.metrics);
    existing.teamAlias ??= team.teamAlias;
    into.set(team.teamId, existing);
  }
}

function mergeDimensions(
  into: Map<string, UsageDimensionRow>,
  from: readonly UsageDimensionRow[],
): void {
  for (const dimension of from) {
    const existing = into.get(dimension.name) ?? {
      name: dimension.name,
      metrics: emptyMetrics(),
    };
    addMetrics(existing.metrics, dimension.metrics);
    into.set(dimension.name, existing);
  }
}

function compareDays(a: UsageDay, b: UsageDay): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}

function compareMetricRows(
  a: { metrics: UsageMetrics },
  b: { metrics: UsageMetrics },
): number {
  return b.metrics.spend - a.metrics.spend;
}

function emptyTeamUsage(): TeamUsage {
  return {
    available: false,
    complete: false,
    totals: emptyMetrics(),
    daily: [],
    byTeam: [],
    models: [],
    modelGroups: [],
    providers: [],
    availableDimensions: {
      models: false,
      modelGroups: false,
      providers: false,
    },
  };
}

/** The not-configured result, so callers do not hand-assemble an empty shape. */
export function notConfiguredTagUsage(
  startDate: DateOnly,
  endDate: DateOnly,
): TagUsage {
  return {
    configured: false,
    available: false,
    startDate,
    endDate,
    windowTotals: emptyMetrics(),
    denominatorSource: "none",
    namespaces: [],
    ignoredTags: [],
    warnings: [],
  };
}
